import { ReactElement, useRef } from "react";
import {
  AriaSelectProps,
  HiddenSelect,
  mergeProps,
  useButton,
  useFocusRing,
  useSelect,
} from "react-aria";
import { useSelectState } from "react-stately";
import cn from "classnames";

import ListBox from "@core/ui/molecules/list-box/ListBox";
import Popover from "@core/ui/molecules/popover/Popover";

import * as styles from "@core/ui/molecules/select/Select.module.scss";

interface SelectProps<T> extends AriaSelectProps<T> {
  adoptionClassName?: string;
}

const Select = <T extends object>(props: SelectProps<T>): ReactElement => {
  const { label, adoptionClassName } = props;
  const state = useSelectState(props);
  const ref = useRef<HTMLButtonElement>(null);
  const { triggerProps, valueProps, menuProps } = useSelect(props, state, ref);
  const { buttonProps } = useButton(triggerProps, ref);
  const { focusProps, isFocusVisible } = useFocusRing();

  return (
    <div className={cn(styles.select, adoptionClassName)}>
      <HiddenSelect state={state} triggerRef={ref} label={props.label} name={props.name} />
      <button
        {...mergeProps(buttonProps, focusProps)}
        ref={ref}
        className={cn(styles.button, {
          [styles.isOpen]: state.isOpen,
          [styles.isFocusVisible]: isFocusVisible,
        })}
      >
        <span {...valueProps} className={styles.value}>
          {state.selectedItem ? state.selectedItem.rendered : label}
        </span>
        <svg aria-hidden="true" role="img" className={styles.selectArrow}>
          <use href="#icon-arrowdown-secondary"></use>
        </svg>
      </button>

      {state.isOpen && (
        <Popover state={state} triggerRef={ref} placement="bottom start">
          <ListBox {...menuProps} state={state} listBoxWidth={ref.current?.offsetWidth} />
        </Popover>
      )}
    </div>
  );
};

export default Select;
